/**
 * RBAC (Role-Based Access Control) Permission Set
 * Defines per-HTTP-verb permissions for a role
 */
export interface RBACPermissions {
  get: boolean;
  post: boolean;
  put: boolean;
  patch: boolean;
  delete: boolean;
}

/**
 * RBAC Role Definition
 * A named role with its associated HTTP-verb permissions.
 * These roles appear in the multi-select dropdown when editing model property access.
 */
export interface RBACRole {
  type: string;
  permission: RBACPermissions;
}

/**
 * RBAC Configuration
 * Project-level role-based access control configuration.
 * Defines all available roles and their permissions globally.
 * A default "public" role is always implicitly available.
 */
export interface RBACConfig {
  /** Whether RBAC is enabled for this project */
  enabled: boolean;
  /** Role definitions with their HTTP-verb permissions */
  roles: RBACRole[];
  /** Whether the RBAC configuration can be modified at runtime (default: false) */
  canModify: boolean;
}

export enum DatabaseType {
  MYSQL = "MySQL",
  POSTGRESQL = "PostgreSQL",
  SQLITE = "SQLite",
  MONGODB = "MongoDB"
}

export enum Runtime {
  NODEJS = "nodejs",
  PYTHON = "python",
  JAVA = "java"
}

export enum StorageType {
  AWS_S3 = "AWS S3",
  GOOGLE_CLOUD = "Google Cloud",
  AZURE_FILE_STORAGE = "Azure File Storage"
}

export enum CloudProvider {
  AWS = "AWS",
  GCP = "GCP",
  AZURE = "Azure"
}

export interface AutoScaleConfig {
  enabled: boolean;
  cloudProvider: CloudProvider;
  minInstances?: number;
  maxInstances?: number;
  targetCPUUtilization?: number;
  targetMemoryUtilization?: number;
  cooldownPeriod?: number;
  scalingPolicy?: "horizontal" | "vertical" | "hybrid";
  // AWS specific
  awsRegion?: string;
  awsAutoScalingGroupName?: string;
  awsLoadBalancerArn?: string;
  // GCP specific
  gcpProjectId?: string;
  gcpZone?: string;
  gcpServiceName?: string;
  // Azure specific
  azureResourceGroup?: string;
  azureLocation?: string;
  azureAppServiceName?: string;
}

export interface AuthenticationMethodsInterface {
  emailPassword: boolean;
  googleAuth: boolean;
}

/**
 * Java-specific configuration for Spring Boot code generation
 */
export interface JavaConfig {
  /** Java version (e.g., 17, 21, 23) */
  javaVersion: '17' | '21' | '23';
  /** Build tool (currently only Maven) */
  buildTool: 'maven';
  /** Maven groupId (e.g., "com.example") */
  groupId: string;
  /** Maven artifactId (e.g., "myapp") */
  artifactId: string;
  /** Base Java package (e.g., "com.example.myapp") */
  basePackage: string;
  /** Spring Boot dependencies to include */
  springDependencies: string[];
}

export interface MetaDataInterface {
  /**
   * The runtime environment. Optional; defaults to "nodejs" if not specified.
   */
  runtime?: Runtime;
  authentication: boolean;
  authenticationMethods: AuthenticationMethodsInterface;
  fileStorage: boolean;
  databaseType: DatabaseType;
  storageType: StorageType;
  rateLimiterEnabled: boolean;
  rateLimiterConfig?: RateLimiterConfig;
  corsEnabled: boolean;
  corsConfig?: CorsConfig;
  websocketEnabled: boolean;
  websocketConfig?: WebSocketConfig;
  autoScaleConfig?: AutoScaleConfig;
  /** Global Role-Based Access Control configuration */
  rbac?: RBACConfig;
  /** Java-specific configuration (only used when runtime is JAVA) */
  javaConfig?: JavaConfig;
}

export interface RateLimiterConfig {
  windowMs: number;
  maxRequests: number;
  max: number;
  standardHeaders: boolean;
  legacyHeaders: boolean;
  message: string;
  skipFailedRequests: boolean;
}

export interface WebSocketConfig {
  path: string;
  corsConfig?: CorsConfig;
}

export interface CorsConfig {
  allowedMethods: ("GET" | "POST" | "PUT" | "DELETE" | "PATCH" | "OPTIONS")[];
  allowedHeaders: string[];
  allowedOrigins: string[];
  allowCredentials: boolean; // Whether to allow credentials (cookies, authorization headers, etc.)
  maxAge?: number; // Optional, specifies how long the results of a preflight request can be cached
  preflightContinue?: boolean; // Optional, if true, the preflight request will not end the response
}